'use client'

import { useTranslations } from 'next-intl'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { useNotificationContext } from '@maestro/commerce-blocks/components/notification/notification.context'

const LOGOUT_REASON_TO_MESSAGE_KEY: Record<string, string> = {
  session_expired: 'session-expired',
  customer_deleted: 'customer-deleted',
}

/**
 * Handles showing a toast notification when the user is redirected back
 * with a logout reason query param (e.g. ?logout=session_expired).
 * Isolated in its own component so that useSearchParams() can be wrapped
 * in a tight Suspense boundary without bailing out the entire app tree.
 */
export default function LogoutReasonNotifier() {
  const searchParams = useSearchParams()
  const { showInfo } = useNotificationContext()
  const t = useTranslations('commerce-blocks.auth')
  const pathname = usePathname()
  const hasShownRef = useRef(false)

  useEffect(() => {
    const logout = searchParams.get('logout')

    if (
      !logout ||
      !LOGOUT_REASON_TO_MESSAGE_KEY[logout] ||
      hasShownRef.current
    ) {
      return
    }

    showInfo(t(LOGOUT_REASON_TO_MESSAGE_KEY[logout]))
    hasShownRef.current = true

    //Maestro B2B hide behind login feature: strip the query param via the
    // History API instead of router.replace() as it  re-fetches the whole RSC payload for login route
    window.history.replaceState(null, '', pathname || '/')
  }, [searchParams, showInfo, t, pathname])

  return null
}
